import { withRouter } from 'react-router';

import PropTypes from 'prop-types';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Grid,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import CommunityOptOutContainer from '../Community/Community/CommunityOptOutContainer';

import ExpandableImageComponent from '../Image/ExpandableImageComponent';

import {
  avatarPlaceholderSRC,
  checkHasPermissions,
  isAdmin,
  isAuthenticated,
} from '../../utils';
import { getFullName } from '../Person/utils';

import permissions from '../../permissions';
import { drawerClosedWidth, drawerOpenedWidth } from '../../staticData';
import { history } from '../../store';
import DropDownMenu from '../DropDownMenu';
import NotificationSettings from '../NotificationPreferencesSettings';
import HeaderBreadcrumbs from './HeaderBreadcrumbs';

const ignoredProps = ['isDrawerOpen'];

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => !ignoredProps.includes(prop),
})(({ theme, isDrawerOpen }) => ({
  position: 'fixed',
  zIndex: theme.zIndex.appBar,
  right: 0,
  height: theme.spacing(10),
  width: `calc(100% - ${
    isDrawerOpen ? drawerOpenedWidth : drawerClosedWidth
  }px)`,
  transition: theme.transitions.create(['width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: '7rem',
  },
}));

const TopNavComponent = (props) => {
  const {
    currentUser,
    showRenderPropsModal,
    toggleCUNotifications,
    hasNewMessages,
    hasNewNotifications,
    isDrawerOpen,
    location: { pathname },
    setDrawerOpen,
    isMobileScreen,
  } = props;

  const theme = useTheme();
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('md'));

  if (!isAuthenticated()) return null;

  const notificationModalProps = {
    title: 'notification preferences',
    maxWidth: 'md',
    render: (props) => <NotificationSettings {...props} />,
  };

  const communityModalProps = {
    title: 'community settings',
    maxWidth: 'md',
    render: (props) => <CommunityOptOutContainer {...props} />,
  };

  const currentUserName = getFullName(currentUser);
  const currentUserCommunities = currentUser?.communities || [];
  const hasCommunityOptOutPermission = checkHasPermissions(
    permissions.opt_out_community,
  );

  const showCommunitySettings =
    !isAdmin() && hasCommunityOptOutPermission && currentUserCommunities.length;

  const menuTitle = isDesktop ? (
    <Grid container wrap="nowrap" alignItems="center">
      {currentUserName}
      <ArrowDropDownIcon fontSize="small" />
    </Grid>
  ) : (
    <div style={{ height: '3.2rem' }}>
      <ExpandableImageComponent
        photo={currentUser?.photo}
        src={avatarPlaceholderSRC}
        caption={`${currentUser?.firstName} ${currentUser?.lastName}`}
        isClickable={false}
        height="3.2rem"
        isSquare
      />
    </div>
  );

  const messagesUrl = pathname.includes('messages')
    ? '/redirect?type=messages&url=messages'
    : '/messages';

  const menuItems = [
    {
      label: 'Notifications',
      onClick: () => toggleCUNotifications(),
      show: !isDesktop && !isAdmin(),
    },
    {
      label: 'Messages',
      onClick: () => history.push(messagesUrl),
      show: !isDesktop,
    },
    {
      label: 'Profile',
      onClick: () => history.push('/global-person-search/profile'),
    },
    {
      label: 'Notification preferences',
      onClick: () => showRenderPropsModal(notificationModalProps),
    },
    {
      label: 'Community settings',
      onClick: () => showRenderPropsModal(communityModalProps),
      show: showCommunitySettings,
    },
    {
      label: 'Log out',
      onClick: () => history.push('/logout'),
      color: theme.palette.secondary.main,
    },
  ];

  return (
    <StyledContainer isDrawerOpen={isDrawerOpen}>
      <AppBar
        position="static"
        color="inherit"
        sx={{
          boxShadow: 'none',
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'row',
          },
        }}
      >
        {isMobileScreen && (
          <Grid>
            <IconButton
              color="inherit"
              onClick={() => setDrawerOpen(!isDrawerOpen)}
              aria-label="Menu icon"
              size="large"
            >
              <MenuIcon style={{ fontSize: '3.1rem', margin: '0.5rem' }} />
            </IconButton>
          </Grid>
        )}
        <Toolbar
          sx={{
            [theme.breakpoints.down('sm')]: {
              flex: 1,
              height: '7rem',
            },
          }}
        >
          <Typography
            variant="h5"
            color="primary"
            sx={{ position: 'absolute', maxWidth: '80%' }}
          >
            <HeaderBreadcrumbs />
          </Typography>
          <div style={{ flexGrow: 1 }} />
          {currentUser?.id ? (
            <Grid
              container
              wrap="nowrap"
              justifyContent="flex-end"
              alignItems="center"
              sx={(theme) => ({
                backgroundColor: theme.palette.common.white,
                position: 'relative',
                width: 'auto',
              })}
            >
              <Box
                sx={{
                  margin: '0 4rem',
                  display: 'flex',
                  flexWrap: 'nowrap',
                  [theme.breakpoints.down('md')]: {
                    display: 'none',
                  },
                }}
              >
                {!isAdmin() ? (
                  <IconButton
                    aria-label="Notifications"
                    sx={{ color: theme.palette.warning.light }}
                    onClick={() => toggleCUNotifications()}
                    size="large"
                  >
                    <Badge
                      color="secondary"
                      variant="dot"
                      overlap="circular"
                      invisible={!hasNewNotifications}
                    >
                      <NotificationsOutlinedIcon sx={{ fontSize: '3rem' }} />
                    </Badge>
                  </IconButton>
                ) : null}

                <IconButton
                  aria-label="Messages"
                  sx={{ color: theme.palette.info.light }}
                  onClick={() => history.push(messagesUrl)}
                  size="large"
                >
                  <Badge
                    color="secondary"
                    variant="dot"
                    overlap="circular"
                    invisible={!hasNewMessages || isAdmin()}
                  >
                    <MailOutlinedIcon sx={{ fontSize: '3rem' }} />
                  </Badge>
                </IconButton>
              </Box>
              <Box
                sx={{
                  [theme.breakpoints.down('md')]: {
                    display: 'none',
                  },
                }}
              >
                <ExpandableImageComponent
                  photo={currentUser?.photo}
                  src={avatarPlaceholderSRC}
                  caption={`${currentUser?.firstName} ${currentUser?.lastName}`}
                  height="3.2rem"
                  isSquare
                />
              </Box>
              <Box sx={{ margin: '0 1rem' }}>
                <DropDownMenu title={menuTitle} items={menuItems} />
              </Box>
            </Grid>
          ) : (
            <Button
              onClick={() => history.push('/logout')}
              variant="contained"
              color="secondary"
              disableElevation
            >
              Log out
            </Button>
          )}
        </Toolbar>
      </AppBar>
    </StyledContainer>
  );
};

TopNavComponent.defaultProps = {
  currentUser: null,
};

TopNavComponent.propTypes = {
  currentUser: PropTypes.instanceOf(Object),
  isMobileScreen: PropTypes.bool.isRequired,
  showRenderPropsModal: PropTypes.func.isRequired,
  toggleCUNotifications: PropTypes.func.isRequired,
  hasNewMessages: PropTypes.bool.isRequired,
  hasNewNotifications: PropTypes.bool.isRequired,
  isDrawerOpen: PropTypes.bool.isRequired,
  location: PropTypes.instanceOf(Object),
  setDrawerOpen: PropTypes.func.isRequired,
};

export default withRouter(TopNavComponent);
